// Annual consumptions calculations (materials, fuels, electricity, water, waste)
// See diagram for formulas and requirements

import { ResidueEntry } from "@/types/residue";
import { WaterEntry, DischargeConfig, SludgeConfig, PollutantEntry, PollutantDoc } from "@/types/water";
import { getFactorValue, convertToKgForCalculation } from "./conversions";

// RESIDUE CALCULATIONS
/**
 * Calculates the carbon footprint for a residue entry
 * Formula: Sum of (quantity × emission factor) for each treatment
 * @param entry The residue entry containing treatments
 * @param getTreatmentInfo Function to get treatment information including emission factor
 * @returns Carbon footprint in kg CO2
 */
export const calculateResidueFootprint = (
  entry: ResidueEntry,
  getTreatmentInfo: (treatmentId: string) =>
    | {
        label: string;
        fe_r?: number;
      }
    | undefined
): number => {
  let footprint = 0;

  for (const treatment of entry.treatments) {
    // Find the emission factor (fe_r) for this treatment type
    const treatmentInfo = getTreatmentInfo(treatment.treatmentTypeId);

    if (treatmentInfo && treatmentInfo.fe_r !== undefined) {
      const fe_r_kg = treatmentInfo.fe_r;
      
      // Sum up quantity × fe_r
      const treatmentFootprint = Number(treatment.quantity) * fe_r_kg;
      footprint += treatmentFootprint;
    } else {
      console.warn(`No emission factor found for treatment type: ${treatment.treatmentTypeId}`);
    }
  }
  
  // Don't round here to preserve precision - will be rounded at display level
  return footprint;
};

/**
 * Sums up the `pc_res` field on every entry,
 * rounding to 2 decimals.
 */
export const calculateTotalResidueFootprint = (entries: (ResidueEntry & { pc_res?: number })[]): number => {
  const total = entries.reduce((sum, entry) => sum + (entry.pc_res || 0), 0);
  // Don't round here to preserve precision - will be rounded at display level
  return total;
};

// ELECTRICITY CALCULATIONS
export const calculateElectricityCarbonFootprint = (
  entry: any,
  sourceDoc?: any
): { footprint: number; factor: number } => {
  if (!sourceDoc || entry.quantity === "") return { footprint: 0, factor: 0 };
  const q = Number(entry.quantity);
  const carbonFactor = sourceDoc.carbon_factor?.value || 0;
  return { footprint: q * carbonFactor, factor: carbonFactor };
};

export const calculateElectricityHydricFootprint = (
  entry: any,
  sourceDoc?: any
): { footprint: number; factor: number } => {
  if (!sourceDoc || entry.quantity === "") return { footprint: 0, factor: 0 };
  const q = Number(entry.quantity);
  const hydricFactor = sourceDoc.hydric_factor?.value || 0;
  return { footprint: q * hydricFactor, factor: hydricFactor };
};

/**
 * Calculate total carbon footprint from all electricity entries
 * @param entries Array of electricity entries
 * @returns Total carbon footprint in kg CO2
 */
export const calculateTotalElectricityCarbonFootprint = (entries: any[]): number => {
  const total = entries.reduce((sum, entry) => sum + (entry.pc_ele || 0), 0);
  // Don't round here to preserve precision - will be rounded at display level
  return total;
};

/**
 * Calculate total hydric footprint from all electricity entries
 * @param entries Array of electricity entries
 * @returns Total hydric footprint in m³
 */
export const calculateTotalElectricityHydricFootprint = (entries: any[]): number => {
  const total = entries.reduce((sum, entry) => sum + (entry.ph_ele || 0), 0);
  // Don't round here to preserve precision - will be rounded at display level
  return total;
};

// MATERIAL CALCULATIONS
export const calculateMaterialCarbonFootprint = (
  entry: any,
  materialDoc?: any
): { footprint: number; factor: number } => {
  if (!materialDoc || entry.quantity === "") return { footprint: 0, factor: 0 };

  // Convert tons to kg if necessary
  const converted = convertToKgForCalculation(Number(entry.quantity), entry.unit);
  const factor = getFactorValue(materialDoc, entry.unit, "carbon");

  // Use converted quantity for calculation but keep original factor for display
  return { footprint: converted.quantity * factor, factor: factor };
};

export const calculateMaterialHydricFootprint = (
  entry: any,
  materialDoc?: any
): { footprint: number; factor: number } => {
  if (!materialDoc || entry.quantity === "") return { footprint: 0, factor: 0 };

  // Convert tons to kg if necessary
  const converted = convertToKgForCalculation(Number(entry.quantity), entry.unit);
  const factor = getFactorValue(materialDoc, entry.unit, "hydric");

  // Use converted quantity for calculation but keep original factor for display
  return { footprint: converted.quantity * factor, factor: factor };
};

/**
 * Calculate total carbon footprint from all material entries
 * @param entries Array of material entries
 * @returns Total carbon footprint in kg CO2
 */
export const calculateTotalMaterialCarbonFootprint = (entries: any[]): number => {
  const total = entries.reduce((sum, entry) => sum + (entry.pc_mat || 0), 0);
  // Don't round here to preserve precision - will be rounded at display level
  return total;
};

/**
 * Calculate total hydric footprint from all material entries
 * @param entries Array of material entries
 * @returns Total hydric footprint in m³
 */
export const calculateTotalMaterialHydricFootprint = (entries: any[]): number => {
  const total = entries.reduce((sum, entry) => sum + (entry.ph_mat || 0), 0);
  // Don't round here to preserve precision - will be rounded at display level
  return total;
};

// FUEL CALCULATIONS
/**
 * Calculate fuel carbon footprint
 * Formula: Sum of (quantity × emission factor) for each fuel
 * @param entry Fuel entry with consumption data
 * @param fuelDoc Fuel document with emission factors
 * @returns Carbon footprint in kg CO2
 */
export const calculateFuelCarbonFootprint = (entry: any, fuelDoc?: any): { footprint: number; factor: number; pci?: number; density?: number } => {
  if (!fuelDoc || entry.quantity === "") return { footprint: 0, factor: 0 };

  const quantity = Number(entry.quantity);
  const pci = fuelDoc.densities?.pci; // PCI in GJ/t
  const density = fuelDoc.densities?.dens_comb; // Density in kg/L
  const fe_comb_GJ = fuelDoc.carbon_factors?.fe_comb_GJ; // Emission factor in kgCO2e/GJ

  let footprint = 0;
  let factor = 0;

  if (entry.unit === "L") {
    // Formula: quantity (L) × density (kg/L) × PCI (GJ/t) × FE (kgCO2e/GJ)
    // Convert L to kg, then to GJ, then apply emission factor
    if (density && pci && fe_comb_GJ) {
      const massKg = quantity * density; // Convert L to kg
      const energyGJ = (massKg / 1000) * pci; // Convert kg to t, then to GJ
      footprint = energyGJ * fe_comb_GJ;
      factor = fe_comb_GJ;
    } else {
      console.warn("Missing required fuel properties for L unit calculation");
      return { footprint: 0, factor: 0 };
    }
    
  } else if (entry.unit === "GJ") {
    factor = fe_comb_GJ || 0;
    footprint = quantity * factor;
    
  } else if (entry.unit === "t") {
    factor = fe_comb_GJ || 0;
    const energyGJ = quantity * pci; // Convert tons to GJ using PCI
    footprint = energyGJ * factor;
    
  } else {
    console.warn(`Unsupported unit for fuel carbon calculation: ${entry.unit}`);
    return { footprint: 0, factor: 0 };
  }

  return {
    footprint,
    factor: factor,
    pci: pci,
    density: density
  };
};

/**
 * Calculate fuel hydric footprint
 * Formula: Sum of (quantity × emission factor) for each fuel
 * @param entry Fuel entry with consumption data
 * @param fuelDoc Fuel document with emission factors
 * @returns Hydric footprint in m³
 */
export const calculateFuelHydricFootprint = (entry: any, fuelDoc?: any): { footprint: number; factor: number; pci?: number; density?: number } => {
  if (!fuelDoc || entry.quantity === "") return { footprint: 0, factor: 0 };

  const quantity = Number(entry.quantity);
  const pci = fuelDoc.densities?.pci; // PCI in GJ/t
  const density = fuelDoc.densities?.dens_comb; // Density in kg/L
  const fh_comb_GJ = fuelDoc.hydric_factors?.fh_comb_GJ; // Hydric factor in m³/GJ

  let footprint = 0;
  let factor = 0;

  if (entry.unit === "L") {
    // Formula: quantity (L) × density (kg/L) × PCI (GJ/t) × FH (m³/GJ)
    // Convert L to kg, then to GJ, then apply hydric factor
    if (density && pci && fh_comb_GJ) {
      const massKg = quantity * density; // Convert L to kg
      const energyGJ = (massKg / 1000) * pci; // Convert kg to t, then to GJ
      footprint = energyGJ * fh_comb_GJ;
      factor = fh_comb_GJ;
    } else {
      console.warn("Missing required fuel properties for L unit calculation");
      return { footprint: 0, factor: 0 };
    }
    
  } else if (entry.unit === "GJ") {
    factor = fh_comb_GJ || 0;
    footprint = quantity * factor;
    
  } else if (entry.unit === "t") {
    factor = fh_comb_GJ || 0;
    const energyGJ = quantity * pci; // Convert tons to GJ using PCI
    footprint = energyGJ * factor;
    
  } else {
    console.warn(`Unsupported unit for fuel hydric calculation: ${entry.unit}`);
    return { footprint: 0, factor: 0 };
  }

  return {
    footprint,
    factor: factor,
    pci: pci,
    density: density
  };
};

/**
 * Calculate total carbon footprint from all fuel entries
 * @param entries Array of fuel entries
 * @returns Total carbon footprint in kg CO2
 */
export const calculateTotalFuelCarbonFootprint = (entries: any[]): number => {
  const total = entries.reduce((sum, entry) => sum + (entry.pc_comb || 0), 0);
  // Don't round here to preserve precision - will be rounded at display level
  return total;
};

/**
 * Calculate total hydric footprint from all fuel entries
 * @param entries Array of fuel entries
 * @returns Total hydric footprint in m³
 */
export const calculateTotalFuelHydricFootprint = (entries: any[]): number => {
  const total = entries.reduce((sum, entry) => sum + (entry.ph_comb || 0), 0);
  // Don't round here to preserve precision - will be rounded at display level
  return total;
};

// WATER CALCULATIONS
/**
 * Calculate total quantity of water used in an entry (sum of all origins)
 * @param entry Water entry with multiple origins
 * @returns Total water quantity in m³
 */
export const calculateWaterEntryTotalQuantity = (entry: WaterEntry): number => {
  const origins = Array.isArray(entry.waterOrigins) ? entry.waterOrigins : [];
  const total = origins.reduce((sum, origin) => {
    return sum + (origin.quantity === "" ? 0 : Number(origin.quantity));
  }, 0);
  // Don't round here to preserve precision - will be rounded at display level
  return total;
};

/**
 * Calculate total hydric footprint for all origins in a water entry
 * For water, the hydric footprint is simply the total quantity (no emission factor)
 * @param entry Water entry with multiple origins
 * @returns Total hydric footprint in m³
 */
export const calculateWaterEntryHydricFootprint = (entry: WaterEntry): number => {
  return calculateWaterEntryTotalQuantity(entry);
};

/**
 * Calculate water output footprint for a single water entry
 * Formula: sum of all water origins quantities
 * @param entry Water entry with multiple origins
 * @returns Water output footprint in m³
 */
export const calculateWaterEntryOutputFootprint = (entry: WaterEntry): number => {
  return calculateWaterEntryTotalQuantity(entry);
};

/**
 * Calculate discharge grey water footprint for a single pollutant
 * Formula: [ac_descargas_polu] = (poludescarga × qdescarga) / (poluvma – polumeio)
 * If user answered "no monitoring of receiving environment", use polumeio = 0
 * @param pollutant Pollutant entry with concentration and monitoring info
 * @param dischargeQuantity Total discharge quantity in m³
 * @param pollutantDoc Pollutant document with VMA value
 * @returns Grey water footprint contribution from this pollutant in m³
 */
export const calculateDischargeGreyWaterForPollutant = (
  pollutant: PollutantEntry,
  dischargeQuantity: number,
  pollutantDoc?: PollutantDoc
): number => {
  if (pollutantDoc?.polu_vma === undefined || pollutantDoc.polu_vma <= 0) {
    return 0;
  }

  if (pollutant.concentration === "" || dischargeQuantity <= 0) {
    return 0;
  }

  const poludescarga = Number(pollutant.concentration); // mg/l
  const qdescarga = dischargeQuantity; // m³
  const poluvma = pollutantDoc.polu_vma; // mg/l

  // Get receiver environment concentration (polumeio)
  // If user answered "no monitoring of receiving environment", use polumeio = 0
  let polumeio = 0; // mg/l
  if (pollutant.hasMonitoring && pollutant.concentrationInReceiver !== undefined && pollutant.concentrationInReceiver !== "") {
    polumeio = Number(pollutant.concentrationInReceiver);
  }

  // Formula: (poludescarga × qdescarga) / (poluvma – polumeio)
  const denominator = poluvma - polumeio;

  // If denominator is <= 0, the calculation is invalid (pollutant exceeds or equals VMA)
  if (denominator <= 0) {
    return 0;
  }

  return (poludescarga * qdescarga) / denominator;
};

/**
 * Calculate discharge grey water footprint for a discharge configuration
 * Formula: ac_descargas = max(ac_descargas_polu) for all pollutants
 * @param discharge Discharge configuration with pollutants and discharge entries
 * @param getPollutantDoc Function to get pollutant document by name
 * @returns Discharge grey water footprint in m³
 */
export const calculateDischargeGreyWaterFootprint = (
  discharge: DischargeConfig,
  getPollutantDoc: (pollutantName: string) => PollutantDoc | undefined
): number => {
  if (!discharge.hasDischarge || discharge.dischargeEntries.length === 0 || !discharge.pollutants || discharge.pollutants.length === 0) {
    return 0;
  }

  // Calculate total discharge quantity from all discharge entries
  const totalDischargeQuantity = discharge.dischargeEntries.reduce((sum, entry) => {
    return sum + (entry.quantity === "" ? 0 : Number(entry.quantity));
  }, 0);

  if (totalDischargeQuantity <= 0) {
    return 0;
  }

  const pollutantFootprints: number[] = [];

  for (const pollutant of discharge.pollutants) {
    const pollutantDoc = getPollutantDoc(pollutant.pollutantName);
    const pollutantFootprint = calculateDischargeGreyWaterForPollutant(pollutant, totalDischargeQuantity, pollutantDoc);
    if (pollutantFootprint > 0) {
      pollutantFootprints.push(pollutantFootprint);
    }
  }

  return pollutantFootprints.length > 0 ? Math.max(...pollutantFootprints) : 0;
};

/**
 * Calculate sludge grey water footprint for a single pollutant
 * Formula: [ac_lamas_polu] = (polulamas × (qlamas × hlamas ÷ 100)) / (poluvma – polumeio)
 * If user answered "no monitoring of receiving environment", use polumeio = 0
 * @param pollutant Pollutant entry with concentration and monitoring info
 * @param sludgeQuantity Total sludge quantity in t
 * @param moistureContent Moisture content percentage (h_lamas)
 * @param pollutantDoc Pollutant document with VMA value
 * @returns Sludge grey water footprint contribution from this pollutant in m³
 */
export const calculateSludgeGreyWaterForPollutant = (
  pollutant: PollutantEntry,
  sludgeQuantity: number,
  moistureContent: number,
  pollutantDoc?: PollutantDoc
): number => {
  if (pollutantDoc?.polu_vma === undefined || pollutantDoc.polu_vma <= 0) {
    return 0;
  }

  if (pollutant.concentration === "" || sludgeQuantity <= 0 || moistureContent <= 0) {
    return 0;
  }

  const polulamas = Number(pollutant.concentration); // mg/l
  const qlamas = sludgeQuantity; // t
  const hlamas = moistureContent; // %
  const poluvma = pollutantDoc.polu_vma; // mg/l

  // Get receiver environment concentration (polumeio)
  // If user answered "no monitoring of receiving environment", use polumeio = 0
  let polumeio = 0; // mg/l
  if (pollutant.hasMonitoring && pollutant.concentrationInReceiver !== undefined && pollutant.concentrationInReceiver !== "") {
    polumeio = Number(pollutant.concentrationInReceiver);
  }

  // Formula: (polulamas × (qlamas × hlamas ÷ 100)) / (poluvma – polumeio)
  const denominator = poluvma - polumeio;

  // If denominator is <= 0, the calculation is invalid (pollutant exceeds or equals VMA)
  if (denominator <= 0) {
    return 0;
  }

  const sludgeWaterContent = (qlamas * hlamas) / 100; // Convert to m³ of water in sludge
  const result = (polulamas * sludgeWaterContent) / denominator;

  return result;
};

/**
 * Calculate sludge grey water footprint for a sludge configuration
 * Formula: ac_lamas = max(ac_lamas_polu) for all pollutants
 * Only applies when sludge stays in company (isDepositedInBasin = true)
 * @param sludge Sludge configuration with pollutants
 * @param getPollutantDoc Function to get pollutant document by name
 * @returns Sludge grey water footprint in m³
 */
export const calculateSludgeGreyWaterFootprint = (
  sludge: SludgeConfig,
  getPollutantDoc: (pollutantName: string) => PollutantDoc | undefined
): number => {
  // Only calculate if sludge exists, stays in company (deposited in basin) and has pollutants
  if (!sludge.hasSludge || sludge.isDepositedInBasin !== true || !sludge.pollutants || sludge.pollutants.length === 0) {
    return 0;
  }

  const sludgeQuantity = sludge.sludgeQuantity === "" ? 0 : Number(sludge.sludgeQuantity);
  const moistureContent = sludge.moistureContent === "" ? 0 : Number(sludge.moistureContent);

  if (sludgeQuantity <= 0 || moistureContent <= 0) {
    return 0;
  }

  const pollutantFootprints: number[] = [];

  for (const pollutant of sludge.pollutants) {
    const pollutantDoc = getPollutantDoc(pollutant.pollutantName);
    const pollutantFootprint = calculateSludgeGreyWaterForPollutant(pollutant, sludgeQuantity, moistureContent, pollutantDoc);
    if (pollutantFootprint > 0) {
      pollutantFootprints.push(pollutantFootprint);
    }
  }

  // Take maximum value among all pollutants (as per specification)
  return pollutantFootprints.length > 0 ? Math.max(...pollutantFootprints) : 0;
};

/**
 * Calculate grey water footprint for a single water entry
 * Main Formula:
 * a) If sludge is sent outside the facility: Água Cinzenta = ac_descargas
 * b) If sludge remains in the facility: Água Cinzenta = ac_descargas + ac_lamas
 * @param entry Water entry with discharge and sludge configuration
 * @param getPollutantDoc Function to get pollutant document by name
 * @returns Grey water footprint in m³
 */
export const calculateWaterEntryGreyFootprint = (
  entry: WaterEntry,
  getPollutantDoc: (pollutantName: string) => PollutantDoc | undefined
): number => {
  // Calculate discharge grey water (ac_descargas)
  const ac_descargas = calculateDischargeGreyWaterFootprint(entry.discharge, getPollutantDoc);

  // Calculate sludge grey water (ac_lamas) - only if sludge stays in facility
  let ac_lamas = 0;
  if (entry.sludge.hasSludge && entry.sludge.isDepositedInBasin === true) {
    ac_lamas = calculateSludgeGreyWaterFootprint(entry.sludge, getPollutantDoc);
  }

  // Main Formula:
  // a) If sludge is sent outside the facility (isDepositedInBasin = false): Água Cinzenta = ac_descargas
  // b) If sludge remains in the facility (isDepositedInBasin = true): Água Cinzenta = ac_descargas + ac_lamas
  const result = ac_descargas + ac_lamas;
  
  // Don't round here to preserve precision - will be rounded at display level
  return result;
};

/**
 * Calculate blue water footprint for a single water entry
 * Formula from diagram: ph_azul = Σ q_agua + sludge_component - Σ q_descargas + [area × (te - tp)]
 * Where sludge_component = (q_lamas × h_lamas ÷ 100) if sludge not deposited in basin, else 0
 * @param entry Water entry
 * @param evaporationRate Taxa de evaporação (te) from IPMA station
 * @param precipitationRate Taxa de precipitação (tp) from IPMA station
 * @returns Blue water footprint for this entry in m³
 */
export const calculateWaterEntryBlueFootprint = (
  entry: WaterEntry,
  evaporationRate?: number,
  precipitationRate?: number
): number => {
  // Start with raw water quantities (Σ q_agua), not the hydric footprint
  let ph_azul = entry.waterOrigins.reduce((sum, origin) => {
    return sum + (origin.quantity === "" ? 0 : Number(origin.quantity));
  }, 0);

  // Add sludge water content if sludge is produced but NOT deposited in basin
  if (entry.sludge.hasSludge && entry.sludge.isDepositedInBasin === false) {
    const sludgeQuantity = entry.sludge.sludgeQuantity === "" ? 0 : Number(entry.sludge.sludgeQuantity);
    const moistureContent = entry.sludge.moistureContent === "" ? 0 : Number(entry.sludge.moistureContent);
    if (sludgeQuantity > 0 && moistureContent > 0) {
      ph_azul += (sludgeQuantity * moistureContent) / 100;
    }
  }

  // Subtract discharge quantities (- Σ q_descargas)
  if (entry.discharge.hasDischarge) {
    const totalDischargeQuantity = entry.discharge.dischargeEntries.reduce((sum, discharge) => {
      return sum + (discharge.quantity === "" ? 0 : Number(discharge.quantity));
    }, 0);
    ph_azul -= totalDischargeQuantity;
  }

  // Add area-based calculation [area × (te - tp)]
  if (entry.waterSurfaceArea !== "" && typeof evaporationRate === "number" && typeof precipitationRate === "number") {
    const surfaceArea = Number(entry.waterSurfaceArea);
    const areaComponent = surfaceArea * (evaporationRate - precipitationRate);
    ph_azul += areaComponent;
  }

  // Don't round here to preserve precision - will be rounded at display level
  return Math.max(0, ph_azul);
};

/**
 * Calculate total water footprint for a single water entry
 * Formula from diagram: ph_azul + ph_cinz
 * @param entry Water entry
 * @returns Total water footprint for this entry in m³
 */
export const calculateWaterEntryTotalFootprint = (entry: WaterEntry): number => {
  const ph_azul = entry.ph_agua_azul || 0;
  const ph_cinz = entry.ph_agua_cinzenta || 0;

  const result = ph_azul + ph_cinz;
  // Don't round here to preserve precision - will be rounded at display level
  return result;
};

/**
 * Calculate total hydric footprint from all water entries
 * @param entries Array of water entries
 * @returns Total hydric footprint in m³
 */
export const calculateTotalWaterHydricFootprint = (entries: WaterEntry[]): number => {
  const total = entries.reduce((sum, entry) => sum + (entry.total_ph_agua || 0), 0);
  // Don't round here to preserve precision - will be rounded at display level
  return total;
};

/**
 * Calculate total water output footprint from all water entries
 * @param entries Array of water entries
 * @returns Total water output footprint in m³
 */
export const calculateTotalWaterOutputFootprint = (entries: WaterEntry[]): number => {
  const total = entries.reduce((sum, entry) => sum + (entry.ph_agua_saida || 0), 0);
  // Don't round here to preserve precision - will be rounded at display level
  return total;
};

/**
 * Calculate total discharge water footprint from all water entries
 * @param entries Array of water entries
 * @returns Total discharge water footprint in m³
 */
export const calculateTotalDischargeWaterFootprint = (entries: WaterEntry[]): number => {
  const total = entries.reduce((sum, entry) => sum + (entry.discharge?.ac_descargas_polu || 0), 0);
  // Don't round here to preserve precision - will be rounded at display level
  return total;
};

/**
 * Calculate total gray water footprint from all water entries
 * @param entries Array of water entries
 * @returns Total gray water footprint in m³
 */
export const calculateTotalGrayWaterFootprint = (entries: WaterEntry[]): number => {
  const total = entries.reduce((sum, entry) => sum + (entry.ph_agua_cinzenta || 0), 0);
  // Don't round here to preserve precision - will be rounded at display level
  return total;
};

/**
 * Calculate total sludge water footprint from all water entries
 * @param entries Array of water entries
 * @returns Total sludge water footprint in m³
 */
export const calculateTotalSludgeWaterFootprint = (entries: WaterEntry[]): number => {
  const total = entries.reduce((sum, entry) => sum + (entry.sludge?.ac_lamas_polu || 0), 0);
  // Don't round here to preserve precision - will be rounded at display level
  return total;
};

/**
 * Calculate total water footprint from all water entries
 * @param entries Array of water entries
 * @returns Total water footprint in m³
 */
export const calculateTotalWaterFootprint = (entries: WaterEntry[]): number => {
  const total = entries.reduce((sum, entry) => sum + (entry.ph_agua_total || 0), 0);
  // Don't round here to preserve precision - will be rounded at display level
  return total;
};
